import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { practiceLocationService } from '../services/practiceLocationService'
import {
  PracticeLocation,
  PracticeLocationFormData,
  PracticeAddress,
  ContactInfo,
} from '../types/practiceLocation'
import './SettingsPracticeLocation.css'

export function SettingsPracticeLocation() {
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState<PracticeLocation | null>(null)

  const { data: practiceLocation, isLoading, error } = useQuery({
    queryKey: ['practice-location'],
    queryFn: () => {
      console.log('[SettingsPracticeLocation] Fetching practice location...')
      return practiceLocationService.getCurrent()
    },
  })

  // Handle errors separately
  useEffect(() => {
    if (error) {
      console.error('[SettingsPracticeLocation] Error loading practice location:', error)
    }
  }, [error])

  // Update formData when practiceLocation changes
  useEffect(() => {
    if (practiceLocation) {
      console.log('[SettingsPracticeLocation] Setting formData from practiceLocation:', practiceLocation)
      setFormData(practiceLocation)
    }
  }, [practiceLocation])

  const updateMutation = useMutation({
    mutationFn: (data: PracticeLocationFormData) => practiceLocationService.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['practice-location'] })
      alert('Practice location updated successfully')
    },
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData) return

    // Validation
    if (!formData.practiceName.trim()) {
      alert('Practice name is required')
      return
    }

    if (formData.addresses.length === 0) {
      alert('At least one address is required')
      return
    }

    if (formData.contacts.length === 0) {
      alert('At least one contact method is required')
      return
    }

    // Validate emails
    const emailContacts = formData.contacts.filter((c) => c.type === 'email')
    for (const contact of emailContacts) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(contact.value)) {
        alert(`Invalid email address: ${contact.value}`)
        return
      }
    }

    try {
      const formDataToSubmit: PracticeLocationFormData = {
        practiceName: formData.practiceName,
        healthcareOrganisationIdentifier: formData.healthcareOrganisationIdentifier || undefined,
        addresses: formData.addresses,
        contacts: formData.contacts,
        primaryCareTeam: formData.primaryCareTeam || undefined,
      }
      await updateMutation.mutateAsync(formDataToSubmit)
    } catch (error: any) {
      alert(error?.message || 'Failed to update practice location')
    }
  }

  const handleReset = () => {
    if (practiceLocation) {
      setFormData({ ...practiceLocation })
    } else {
      setFormData(null)
    }
  }

  const handlePracticeNameChange = (value: string) => {
    if (!formData) return
    setFormData({ ...formData, practiceName: value })
  }

  const handleHOIChange = (value: string) => {
    if (!formData) return
    setFormData({ ...formData, healthcareOrganisationIdentifier: value || undefined })
  }

  const handleAddAddress = () => {
    if (!formData) return
    const newAddress: PracticeAddress = {
      id: `addr-${Date.now()}`,
      type: 'branch',
      name: '',
      addressLine1: '',
      city: '',
      postcode: '',
      country: 'Ireland',
      isActive: true,
    }
    setFormData({ ...formData, addresses: [...formData.addresses, newAddress] })
  }

  const handleRemoveAddress = (id: string) => {
    if (!formData) return
    if (formData.addresses.length === 1) {
      alert('At least one address is required')
      return
    }
    setFormData({
      ...formData,
      addresses: formData.addresses.filter((addr) => addr.id !== id),
    })
  }

  const handleAddressChange = (id: string, field: keyof PracticeAddress, value: any) => {
    if (!formData) return
    setFormData({
      ...formData,
      addresses: formData.addresses.map((addr) => (addr.id === id ? { ...addr, [field]: value } : addr)),
    })
  }

  const handleSetPrimaryAddress = (id: string) => {
    if (!formData) return
    setFormData({
      ...formData,
      addresses: formData.addresses.map((addr) => ({
        ...addr,
        type: addr.id === id ? 'primary' : 'branch',
      })),
    })
  }

  const handleAddContact = () => {
    if (!formData) return
    const newContact: ContactInfo = {
      id: `contact-${Date.now()}`,
      type: 'telephone',
      value: '',
      label: '',
      isPrimary: false,
    }
    setFormData({ ...formData, contacts: [...formData.contacts, newContact] })
  }

  const handleRemoveContact = (id: string) => {
    if (!formData) return
    if (formData.contacts.length === 1) {
      alert('At least one contact method is required')
      return
    }
    setFormData({
      ...formData,
      contacts: formData.contacts.filter((contact) => contact.id !== id),
    })
  }

  const handleContactChange = (id: string, field: keyof ContactInfo, value: any) => {
    if (!formData) return
    setFormData({
      ...formData,
      contacts: formData.contacts.map((contact) => (contact.id === id ? { ...contact, [field]: value } : contact)),
    })
  }

  const handleSetPrimaryContact = (id: string) => {
    if (!formData) return
    setFormData({
      ...formData,
      contacts: formData.contacts.map((contact) => ({
        ...contact,
        isPrimary: contact.id === id,
      })),
    })
  }

  const handlePrimaryCareTeamChange = (field: 'name' | 'code', value: string) => {
    if (!formData) return
    setFormData({
      ...formData,
      primaryCareTeam: {
        ...formData.primaryCareTeam,
        [field]: value || undefined,
      },
    })
  }

  if (isLoading) {
    return <div className="loading">Loading practice location...</div>
  }

  if (error) {
    console.error('[SettingsPracticeLocation] Error:', error)
    return (
      <div className="error">
        Failed to load practice location: {error instanceof Error ? error.message : 'Unknown error'}
      </div>
    )
  }

  // Use practiceLocation directly if formData hasn't been set yet
  const displayData = formData || practiceLocation

  if (!displayData) {
    return <div className="error">Failed to load practice location (no data available)</div>
  }

  return (
    <div className="settings-practice-location">
      <div className="settings-section-header">
        <h2>Practice Location</h2>
        <p>Manage practice information, addresses, contact details, and primary care team information.</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="practice-location-form">
          {/* Practice Information */}
          <div className="form-section">
            <h3>Practice Information</h3>
            <div className="form-group">
              <label htmlFor="practiceName">
                Practice Name <span className="required">*</span>
              </label>
              <input
                id="practiceName"
                type="text"
                value={displayData.practiceName}
                onChange={(e) => handlePracticeNameChange(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="hoi">Healthcare Organisation Identifier (HOI)</label>
              <input
                id="hoi"
                type="text"
                value={displayData.healthcareOrganisationIdentifier || ''}
                onChange={(e) => handleHOIChange(e.target.value)}
                placeholder="Optional"
              />
            </div>
          </div>

          {/* Addresses */}
          <div className="form-section">
            <div className="section-header-with-action">
              <h3>Addresses</h3>
              <button type="button" className="btn-add" onClick={handleAddAddress}>
                + Add Address
              </button>
            </div>
            <p className="section-description">
              Add practice premises addresses, including branch surgeries. Mark one as primary.
            </p>
            {displayData.addresses.map((address, index) => (
              <div key={address.id} className="address-card">
                <div className="card-header">
                  <div className="card-title">
                    {address.type === 'primary' ? (
                      <span className="primary-badge">Primary Address</span>
                    ) : (
                      <span className="branch-badge">
                        {address.name || `Branch Surgery ${index}`}
                      </span>
                    )}
                  </div>
                  <div className="card-actions">
                    {address.type !== 'primary' && (
                      <button
                        type="button"
                        className="btn-set-primary"
                        onClick={() => handleSetPrimaryAddress(address.id)}
                      >
                        Set as Primary
                      </button>
                    )}
                    {displayData.addresses.length > 1 && (
                      <button
                        type="button"
                        className="btn-remove"
                        onClick={() => handleRemoveAddress(address.id)}
                        title="Remove address"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
                <div className="card-content">
                  {address.type === 'branch' && (
                    <div className="form-group">
                      <label>Branch Name</label>
                      <input
                        type="text"
                        value={address.name || ''}
                        onChange={(e) => handleAddressChange(address.id, 'name', e.target.value)}
                        placeholder="e.g., Main Surgery, Branch Office"
                      />
                    </div>
                  )}
                  <div className="form-group">
                    <label>
                      Address Line 1 <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={address.addressLine1}
                      onChange={(e) => handleAddressChange(address.id, 'addressLine1', e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Address Line 2</label>
                    <input
                      type="text"
                      value={address.addressLine2 || ''}
                      onChange={(e) => handleAddressChange(address.id, 'addressLine2', e.target.value || undefined)}
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        City <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        value={address.city}
                        onChange={(e) => handleAddressChange(address.id, 'city', e.target.value)}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>County</label>
                      <input
                        type="text"
                        value={address.county || ''}
                        onChange={(e) => handleAddressChange(address.id, 'county', e.target.value || undefined)}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        Postcode <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        value={address.postcode}
                        onChange={(e) => handleAddressChange(address.id, 'postcode', e.target.value)}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        Country <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        value={address.country}
                        onChange={(e) => handleAddressChange(address.id, 'country', e.target.value)}
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Contact Information */}
          <div className="form-section">
            <div className="section-header-with-action">
              <h3>Contact Information</h3>
              <button type="button" className="btn-add" onClick={handleAddContact}>
                + Add Contact
              </button>
            </div>
            <p className="section-description">
              Add telephone, mobile, fax numbers, and email addresses. Mark one as primary.
            </p>
            {displayData.contacts.map((contact) => (
              <div key={contact.id} className="contact-card">
                <div className="card-header">
                  <div className="card-title">
                    {contact.isPrimary && <span className="primary-badge">Primary</span>}
                    <select
                      value={contact.type}
                      onChange={(e) =>
                        handleContactChange(contact.id, 'type', e.target.value as ContactInfo['type'])
                      }
                      className="contact-type-select"
                    >
                      <option value="telephone">Telephone</option>
                      <option value="mobile">Mobile</option>
                      <option value="fax">Fax</option>
                      <option value="email">Email</option>
                    </select>
                  </div>
                  <div className="card-actions">
                    {!contact.isPrimary && (
                      <button
                        type="button"
                        className="btn-set-primary"
                        onClick={() => handleSetPrimaryContact(contact.id)}
                      >
                        Set as Primary
                      </button>
                    )}
                    {displayData.contacts.length > 1 && (
                      <button
                        type="button"
                        className="btn-remove"
                        onClick={() => handleRemoveContact(contact.id)}
                        title="Remove contact"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
                <div className="card-content">
                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        {contact.type === 'email' ? 'Email Address' : 'Number'} <span className="required">*</span>
                      </label>
                      <input
                        type={contact.type === 'email' ? 'email' : 'tel'}
                        value={contact.value}
                        onChange={(e) => handleContactChange(contact.id, 'value', e.target.value)}
                        required
                        placeholder={
                          contact.type === 'email'
                            ? 'example@clinic.ie'
                            : contact.type === 'mobile'
                            ? '+353 87 123 4567'
                            : contact.type === 'fax'
                            ? '+353 1 234 5678'
                            : '+353 1 234 5678'
                        }
                      />
                    </div>
                    <div className="form-group">
                      <label>Label</label>
                      <input
                        type="text"
                        value={contact.label || ''}
                        onChange={(e) => handleContactChange(contact.id, 'label', e.target.value || undefined)}
                        placeholder="e.g., Main, Reception, Emergency"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Primary Care Team */}
          <div className="form-section">
            <h3>Primary Care Team</h3>
            <p className="section-description">Primary care team name and code (if available).</p>
            <div className="form-row">
              <div className="form-group">
                <label>Team Name</label>
                <input
                  type="text"
                  value={displayData.primaryCareTeam?.name || ''}
                  onChange={(e) => handlePrimaryCareTeamChange('name', e.target.value)}
                  placeholder="Optional"
                />
              </div>
              <div className="form-group">
                <label>Team Code</label>
                <input
                  type="text"
                  value={displayData.primaryCareTeam?.code || ''}
                  onChange={(e) => handlePrimaryCareTeamChange('code', e.target.value)}
                  placeholder="Optional"
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={handleReset}>
              Reset
            </button>
            <button type="submit" className="btn-primary" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}
